import { Grid, Typography } from '@material-ui/core';
import { useScrollButtonState } from '@ncr-retail/hooks';
import React from 'react';
import { EJRecordTypes } from 'src/common/enumerations/ej-record-types';
import { IElectronicJournalRecord } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { useTranslation } from 'react-i18next';
import { SvgCoupon, SvgCouponActive } from 'src/item-list/components/item-list-icons';
import Pagination from 'src/pagination/pagination';
import { couponsQuantityfn } from '../coupon-quatity-helper';
import { styles } from './summary-menu-styles-r10';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';

export const CouponSummaryGroupView: React.FC<CouponSummaryMenuProps> = ({
  title,
  items,
  testIdPrefix,
}): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();
  const ejList = items.filter(i => i.Type !== EJRecordTypes.Product);
  const unusedCoupon = ejList.filter(
    i => i.Type === EJRecordTypes.Coupon && !i.Voided && (i.CouponRedemptionState?.Unused ?? 0),
  );
  const redeemedCoupon = ejList.filter(
    i => i.Type === EJRecordTypes.Coupon && !i.Voided && (i.CouponRedemptionState?.Redeemed ?? 0),
  );
  const issuedCoupon = ejList.filter(i => i.Type === EJRecordTypes.IssuedCoupon && !i.Voided);

  const [
    {
      currentPage: redeemedCouponContainerCurrentPage,
      lastPage: redeemedCouponContainerLastPage,
      onPageChanged: redeemedCouponContainerPageChanged,
    },
    scrollRef,
  ] = useScrollButtonState(redeemedCoupon.length || 1);
  const [
    {
      currentPage: unusedCouponContainerCurrentPage,
      lastPage: unusedCouponContainerLastPage,
      onPageChanged: unusedCouponContainerPageChanged,
    },
    scrollRef2,
  ] = useScrollButtonState(unusedCoupon.length || 1);
  const [
    {
      currentPage: issuedCouponContainerCurrentPage,
      lastPage: issuedCouponContainerLastPage,
      onPageChanged: issuedCouponContainerPageChanged,
    },
    scrollRef3,
  ] = useScrollButtonState(issuedCoupon.length || 1);

  const couponList = (coupon: IElectronicJournalRecord[]): IElectronicJournalRecord[] => {
    const uniqueCouponList: IElectronicJournalRecord[] = [];
    coupon.map(x =>
      uniqueCouponList.filter(a => a.Name === x.Name).length > 0 ? null : uniqueCouponList.push(x),
    );
    return uniqueCouponList;
  };

  const uniqueIssuedCoupon = couponList(issuedCoupon);
  const xlrConfig = getCurrentXLRConfig();

  return (
    <Grid
      container
      direction={'column'}
      className={`fullHeight ${classes.dialogPadding} ${classes.couponSummaryWrapper}`}
      justify={'flex-start'}
      data-testid={`${testIdPrefix}-summary-menu`}
    >
      {/* summary */}
      <Grid container justify='space-between' className={'fullWidth'}>
        <Typography
          className={classes.summaryLabel}
          data-testid={`${testIdPrefix}-summary-menu-label`}
        >
          {title} (
          {ejList
            .filter(
              i =>
                !i.Voided &&
                (i.Type === EJRecordTypes.Coupon || i.Type === EJRecordTypes.IssuedCoupon),
            )
            .reduce((total, current) => {
              return total + (current?.Quantity ?? 0);
            }, 0)}
          )
        </Typography>
      </Grid>
      <Grid className={classes.couponTypesGrid}>
        {/* redeemed */}
        <Grid container className={classes.couponSummaryContainer}>
          <Grid item className={`fullWidth ${classes.couponSummaryItemContainer}`}>
            <div
              ref={scrollRef}
              data-testid={`${testIdPrefix}-summary-list-container`}
              className='verticalScroll fullWidth fullHeight'
            >
              <Grid container className={classes.rowRelative}>
                <SvgCouponActive fill='#54AF48' className={classes.iconStyle} />
                <Typography
                  className={classes.couponSubHeading}
                  data-testid={`${testIdPrefix}-summary-menu-accepted-coupon-label`}
                >
                  {t('AllCouponView_AcceptedCoupons')} (
                  {redeemedCoupon.reduce((total, current) => {
                    return total + (current.CouponRedemptionState?.Redeemed ?? 0);
                  }, 0)}
                  )
                </Typography>
              </Grid>
              <Grid container className={classes.rowRelative}>
                {couponList(redeemedCoupon).map((item, index) => {
                  return (
                    <>
                      <Grid
                        container
                        key={index}
                        className={classes.summaryText}
                        data-testid={`${testIdPrefix}-summary-accepted-content-${item.Name}`}
                      >
                        {item.Name} ({couponsQuantityfn(redeemedCoupon, true)[item.Name]})
                      </Grid>
                      {ejList.map(detailItem => {
                        if (
                          detailItem.PromotionId &&
                          item.CouponRedemptionsIds?.includes(detailItem.PromotionId as string)
                        ) {
                          return (
                            <Grid
                              key={detailItem.PromotionId}
                              container
                              className={classes.detailLine}
                            >
                              <Grid item className={classes.detailItem}>
                                <span
                                  data-testid={`coupon-summary-item-detail-line-${detailItem.PromotionId}`}
                                  className={classes.promotionDescription}
                                >
                                  {detailItem.Description}
                                </span>
                              </Grid>
                            </Grid>
                          );
                        } else {
                          return null;
                        }
                      })}
                    </>
                  );
                })}
              </Grid>
            </div>
          </Grid>
          <Grid className={`fullWidth ${classes.footer}`}>
            <Grid container direction='row'>
              <Grid item container justify='space-evenly' className={classes.paginationContainer}>
                <Pagination
                  currentPage={redeemedCouponContainerCurrentPage}
                  totalPages={redeemedCouponContainerLastPage}
                  updateCurrentPageHandler={redeemedCouponContainerPageChanged}
                  testIdPrefix={'accepted-coupon-summary'}
                />
              </Grid>
            </Grid>
          </Grid>
          <div
            className={`${classes.unusedCouponContainerSeparator} ${classes.couponContainerSeparator}`}
          ></div>
        </Grid>
        {/* unused */}
        {(unusedCoupon.length > 0 ||
          (unusedCoupon.length === 0 && xlrConfig.showZeroUnusedCoupons)) && (
          <Grid container className={classes.couponSummaryContainer}>
            <Grid container>
              <Grid
                item
                className={`fullWidth ${classes.couponSummaryItemContainer} ${
                  unusedCouponContainerLastPage <= 1 ? classes.customMargin : ''
                }`}
              >
                <div
                  ref={scrollRef2}
                  data-testid={`${testIdPrefix}-summary-list-container`}
                  className='verticalScroll fullWidth fullHeight'
                >
                  <Grid container className={classes.rowRelative}>
                    <SvgCoupon className={classes.iconStyle} />
                    <Typography
                      className={classes.couponSubHeading}
                      data-testid={`${testIdPrefix}-summary-menu-unused-coupon-label`}
                    >
                      {t('AllCouponView_UnusedCoupons')} (
                      {unusedCoupon.reduce((total, current) => {
                        return total + (current.CouponRedemptionState?.Unused ?? 0);
                      }, 0)}
                      )
                    </Typography>
                  </Grid>
                  <Grid container className={classes.rowRelative}>
                    {couponList(unusedCoupon).map((item, index) => {
                      return (
                        <Grid
                          container
                          key={index}
                          className={classes.summaryText}
                          data-testid={`${testIdPrefix}-summary-unused-content-${item.Name}`}
                        >
                          {item.Name} ({couponsQuantityfn(unusedCoupon, false)[item.Name]})
                        </Grid>
                      );
                    })}
                  </Grid>
                </div>
              </Grid>
              <Grid className={`fullWidth ${classes.footer}`}>
                <Grid container direction='row'>
                  <Grid
                    item
                    container
                    justify='space-evenly'
                    className={classes.paginationContainer}
                  >
                    <Pagination
                      currentPage={unusedCouponContainerCurrentPage}
                      totalPages={unusedCouponContainerLastPage}
                      updateCurrentPageHandler={unusedCouponContainerPageChanged}
                      testIdPrefix={'unused-coupon-summary'}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <div
              className={`${classes.issuedCouponContainerSeparator} ${classes.couponContainerSeparator}`}
            ></div>
          </Grid>
        )}
        {/* issued */}
        {(issuedCoupon.length > 0 ||
          (issuedCoupon.length === 0 && xlrConfig.showZeroIssuedCoupons)) && (
          <Grid container className={classes.couponSummaryContainer}>
            <Grid
              item
              className={`fullWidth ${classes.couponSummaryItemContainer} ${
                issuedCouponContainerLastPage <= 1 ? classes.customMargin : ''
              }`}
            >
              <div
                ref={scrollRef3}
                data-testid={`${testIdPrefix}-summary-list-container`}
                className='verticalScroll fullWidth fullHeight'
              >
                <Grid container className={classes.rowRelative}>
                  <SvgCoupon className={classes.iconStyle} />
                  <Typography
                    className={classes.couponSubHeading}
                    data-testid={`${testIdPrefix}-summary-menu-issued-coupon-label`}
                  >
                    {t('AllCouponView_IssuedCoupons')} ({uniqueIssuedCoupon.length})
                  </Typography>
                </Grid>
                <Grid container className={classes.rowRelative}>
                  {uniqueIssuedCoupon.map((item, index) => {
                    return (
                      <Grid
                        container
                        key={index}
                        className={classes.summaryText}
                        data-testid={`${testIdPrefix}-summary-issued-content-${item.Name}`}
                      >
                        {item.Name} ({couponsQuantityfn(issuedCoupon, false)[item.Name]})
                      </Grid>
                    );
                  })}
                </Grid>
              </div>
            </Grid>
            <Grid className={`fullWidth ${classes.footer}`}>
              <Grid container direction='row'>
                <Grid item container justify='space-evenly' className={classes.paginationContainer}>
                  <Pagination
                    currentPage={issuedCouponContainerCurrentPage}
                    totalPages={issuedCouponContainerLastPage}
                    updateCurrentPageHandler={issuedCouponContainerPageChanged}
                    testIdPrefix={'issued-coupon-summary'}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

interface CouponSummaryMenuProps {
  title: string;
  items: IElectronicJournalRecord[];
  testIdPrefix: string;
}
